import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Cloud, Sun, CloudRain, CloudSnow, CloudLightning, Wind, Droplets, 
  Thermometer, Eye, Sunrise, Sunset, ChevronRight, Radio, Newspaper
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const weatherIcons = {
  0: Sun, 1: Sun, 2: Cloud, 3: Cloud,
  45: Cloud, 48: Cloud, 51: CloudRain, 53: CloudRain, 55: CloudRain,
  61: CloudRain, 63: CloudRain, 65: CloudRain,
  71: CloudSnow, 73: CloudSnow, 75: CloudSnow, 77: CloudSnow,
  80: CloudRain, 81: CloudRain, 82: CloudRain,
  85: CloudSnow, 86: CloudSnow,
  95: CloudLightning, 96: CloudLightning, 99: CloudLightning
};

const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const WeatherNewsPage = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeather();
    const interval = setInterval(fetchWeather, 600000); // Refresh every 10 min
    return () => clearInterval(interval);
  }, []);

  const fetchWeather = async () => {
    try {
      const res = await axios.get(`${API}/weather/detroit`);
      setWeather(res.data);
    } catch (e) {
      console.error("Weather fetch failed:", e);
    } finally {
      setLoading(false);
    }
  };

  const getWeatherIcon = (code) => {
    const Icon = weatherIcons[code] || Cloud;
    return Icon;
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  };

  const formatDay = (dateStr) => {
    const d = new Date(dateStr + "T12:00:00");
    return dayNames[d.getDay()];
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + "T12:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <Cloud className="w-12 h-12 text-neon-red mx-auto" />
          </motion.div>
          <p className="font-mono text-sm text-chrome-mid mt-4">LOADING DETROIT WEATHER...</p>
        </div>
      </div>
    );
  }

  const CurrentIcon = weather ? getWeatherIcon(weather.current.weather_code) : Cloud;

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 md:px-6" data-testid="weather-news-page">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <div className="red-line h-12" />
          <div>
            <p className="font-mono text-xs tracking-[0.3em] text-neon-red mb-1">HOT LIVE 95 DETROIT</p>
            <h1 className="font-display text-3xl md:text-4xl chrome-text">WEATHER & NEWS</h1>
          </div>
        </motion.div>

        {weather && (
          <>
            {/* Current Weather - Hero Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-surface/80 backdrop-blur-md border border-white/10 p-6 md:p-8 mb-6"
              data-testid="current-weather"
            >
              <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6">
                {/* Left: Temp & Condition */}
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <CurrentIcon className="w-20 h-20 md:w-24 md:h-24 text-neon-red" />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-neon-red rounded-full animate-pulse" />
                  </div>
                  <div>
                    <p className="text-6xl md:text-7xl font-display chrome-text leading-none" data-testid="current-temp">
                      {Math.round(weather.current.temperature)}°
                    </p>
                    <p className="text-chrome-mid text-base md:text-lg mt-1" data-testid="current-condition">
                      {weather.current.condition}
                    </p>
                    <p className="font-mono text-xs text-neon-red tracking-widest mt-2">
                      {weather.location}
                    </p>
                  </div>
                </div>

                {/* Right: Details Grid */}
                <div className="grid grid-cols-2 gap-4 md:gap-6">
                  <div className="flex items-center gap-3">
                    <Thermometer className="w-5 h-5 text-chrome-mid flex-shrink-0" />
                    <div>
                      <p className="text-xs text-chrome-dark">FEELS LIKE</p>
                      <p className="text-white font-medium">{Math.round(weather.current.feels_like)}°F</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Droplets className="w-5 h-5 text-chrome-mid flex-shrink-0" />
                    <div>
                      <p className="text-xs text-chrome-dark">HUMIDITY</p>
                      <p className="text-white font-medium">{weather.current.humidity}%</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Wind className="w-5 h-5 text-chrome-mid flex-shrink-0" />
                    <div>
                      <p className="text-xs text-chrome-dark">WIND</p>
                      <p className="text-white font-medium">{Math.round(weather.current.wind_speed)} mph</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Eye className="w-5 h-5 text-chrome-mid flex-shrink-0" />
                    <div>
                      <p className="text-xs text-chrome-dark">DIRECTION</p>
                      <p className="text-white font-medium">{weather.current.wind_direction}°</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Hourly Forecast */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-surface/80 backdrop-blur-md border border-white/10 p-6 mb-6"
              data-testid="hourly-forecast"
            >
              <p className="font-mono text-xs tracking-widest text-neon-red mb-4">24-HOUR FORECAST</p>
              <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                {weather.hourly.filter((_, i) => i % 2 === 0).slice(0, 12).map((hour, i) => {
                  const HourIcon = getWeatherIcon(hour.weather_code);
                  return (
                    <div key={i} className="flex flex-col items-center gap-2 min-w-[60px]">
                      <p className="text-xs text-chrome-dark">{formatTime(hour.time)}</p>
                      <HourIcon className="w-5 h-5 text-chrome-mid" />
                      <p className="text-white text-sm font-medium">{Math.round(hour.temperature)}°</p>
                      {hour.precipitation_probability > 0 && (
                        <p className="text-xs text-blue-400">{hour.precipitation_probability}%</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* 7-Day Forecast */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-surface/80 backdrop-blur-md border border-white/10 p-6 mb-6"
              data-testid="daily-forecast"
            >
              <p className="font-mono text-xs tracking-widest text-neon-red mb-4">7-DAY FORECAST</p>
              <div className="space-y-3">
                {weather.daily.map((day, i) => {
                  const DayIcon = getWeatherIcon(day.weather_code);
                  const tempRange = day.temp_max - day.temp_min;
                  const minAll = Math.min(...weather.daily.map(d => d.temp_min));
                  const maxAll = Math.max(...weather.daily.map(d => d.temp_max));
                  const rangeAll = maxAll - minAll || 1;
                  const leftPct = ((day.temp_min - minAll) / rangeAll) * 100;
                  const widthPct = (tempRange / rangeAll) * 100;
                  
                  return (
                    <div key={i} className="flex items-center gap-4" data-testid={`forecast-day-${i}`}>
                      <p className="text-sm text-chrome-mid w-10 flex-shrink-0">{i === 0 ? "Today" : formatDay(day.date)}</p>
                      <p className="text-xs text-chrome-dark w-16 flex-shrink-0 hidden md:block">{formatDate(day.date)}</p>
                      <DayIcon className="w-5 h-5 text-chrome-mid flex-shrink-0" />
                      <p className="text-xs text-chrome-dark w-20 flex-shrink-0 hidden sm:block">{day.condition}</p>
                      <p className="text-sm text-blue-400 w-10 flex-shrink-0 text-right">{Math.round(day.temp_min)}°</p>
                      {/* Temp bar */}
                      <div className="flex-1 h-1.5 bg-white/5 rounded-full relative">
                        <div 
                          className="absolute h-full rounded-full"
                          style={{
                            left: `${leftPct}%`,
                            width: `${Math.max(widthPct, 5)}%`,
                            background: `linear-gradient(to right, #3b82f6, #ef4444)`
                          }}
                        />
                      </div>
                      <p className="text-sm text-neon-red w-10 flex-shrink-0">{Math.round(day.temp_max)}°</p>
                      {day.precipitation_probability > 20 && (
                        <span className="text-xs text-blue-400 flex items-center gap-1 w-12 flex-shrink-0">
                          <Droplets className="w-3 h-3" />{day.precipitation_probability}%
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Sunrise/Sunset */}
              {weather.daily[0] && (
                <div className="flex items-center gap-8 mt-6 pt-4 border-t border-white/5">
                  <div className="flex items-center gap-2">
                    <Sunrise className="w-4 h-4 text-yellow-500" />
                    <span className="text-xs text-chrome-mid">Sunrise {formatTime(weather.daily[0].sunrise)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Sunset className="w-4 h-4 text-orange-500" />
                    <span className="text-xs text-chrome-mid">Sunset {formatTime(weather.daily[0].sunset)}</span>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Detroit News & Station Updates */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-surface/80 backdrop-blur-md border border-white/10 p-6"
              data-testid="news-section"
            >
              <p className="font-mono text-xs tracking-widest text-neon-red mb-4 flex items-center gap-2">
                <Newspaper className="w-4 h-4" /> DETROIT NEWS & STATION UPDATES
              </p>
              <div className="space-y-4">
                <a href="https://www.detroitnews.com" target="_blank" rel="noopener noreferrer" className="block p-4 bg-black/30 border border-white/5 hover:border-neon-red/30 transition-colors group">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-white group-hover:text-neon-red transition-colors font-medium">The Detroit News</p>
                      <p className="text-chrome-mid text-sm mt-1">Latest headlines from Detroit's premier news source</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-chrome-dark group-hover:text-neon-red flex-shrink-0 mt-1" />
                  </div>
                </a>
                <a href="https://www.freep.com" target="_blank" rel="noopener noreferrer" className="block p-4 bg-black/30 border border-white/5 hover:border-neon-red/30 transition-colors group">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-white group-hover:text-neon-red transition-colors font-medium">Detroit Free Press</p>
                      <p className="text-chrome-mid text-sm mt-1">Community news, sports, and entertainment from the Motor City</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-chrome-dark group-hover:text-neon-red flex-shrink-0 mt-1" />
                  </div>
                </a>
                <a href="https://www.clickondetroit.com" target="_blank" rel="noopener noreferrer" className="block p-4 bg-black/30 border border-white/5 hover:border-neon-red/30 transition-colors group">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-white group-hover:text-neon-red transition-colors font-medium">Click On Detroit (WDIV)</p>
                      <p className="text-chrome-mid text-sm mt-1">Local TV news, weather alerts, and traffic updates</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-chrome-dark group-hover:text-neon-red flex-shrink-0 mt-1" />
                  </div>
                </a>
                <div className="p-4 bg-neon-red/5 border border-neon-red/20">
                  <div className="flex items-center gap-3">
                    <Radio className="w-5 h-5 text-neon-red flex-shrink-0" />
                    <div>
                      <p className="text-neon-red font-medium text-sm">Hot Live 95 Station Update</p>
                      <p className="text-chrome-mid text-sm mt-1">
                        Now streaming 24/7 — Detroit's finest A.I. radio bringing you the best of Blues, Soul, R&B, and more.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}

        {!weather && !loading && (
          <div className="text-center py-20">
            <Cloud className="w-16 h-16 text-chrome-mid mx-auto mb-4" />
            <p className="text-chrome-mid">Weather data unavailable. Please try again later.</p>
          </div>
        )}

        {/* Attribution */}
        <p className="text-center text-chrome-dark text-xs mt-6 font-mono">
          Weather data provided by Open-Meteo.com • Updated every 10 minutes
        </p>
      </div>
    </div>
  );
};

export default WeatherNewsPage;
